import React, { useState, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './GitContributors.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CHART_COLORS = [
  '#1850ff', '#ff6b6b', '#51cf66', '#fcc419', '#845ef7',
  '#ff922b', '#22b8cf', '#e64980', '#20c997', '#adb5bd'
];

const tooltipStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  titleColor: '#121b29',
  bodyColor: '#27303e',
  borderColor: '#dce2ec',
  borderWidth: 1,
  padding: 12,
  titleFont: { size: 14, weight: 'bold' },
  bodyFont: { size: 12 }
};

const axisStyle = {
  ticks: { color: '#6b788d', font: { size: 12 } },
  grid: { color: 'rgba(0, 0, 0, 0.05)' }
};

const xAxisStyle = {
  ticks: { color: '#6b788d', font: { size: 12 } },
  grid: { display: false }
};

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: tooltipStyle
  }
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const WORK_HOUR_BG = '#cdd1d8';

const getHeatmapColor = (value, maxValue) => {
  if (!value || value === 0) return '#ebedf0';
  const intensity = Math.min(value / Math.max(maxValue, 1), 1);
  if (intensity <= 0.25) return '#9be9a8';
  if (intensity <= 0.5) return '#40c463';
  if (intensity <= 0.75) return '#30a14e';
  return '#216e39';
};

const HeatmapNumberedLegend = ({ maxValue }) => {
  if (maxValue <= 0) return null;
  const steps = [];
  if (maxValue <= 4) {
    for (let i = 0; i <= maxValue; i++) steps.push(i);
  } else {
    const count = Math.min(maxValue, 5);
    for (let i = 0; i <= count; i++) {
      steps.push(Math.round((i / count) * maxValue));
    }
  }
  // Deduplicate
  const unique = [...new Set(steps)];

  return (
    <div className="gc-heatmap-legend">
      {unique.map((val, i) => (
        <div key={i} className="gc-legend-item">
          <div
            className="gc-heatmap-legend-cell"
            style={{ backgroundColor: getHeatmapColor(val, maxValue) }}
          />
          <span className="gc-legend-number">{val}</span>
        </div>
      ))}
      <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">commits</span>
    </div>
  );
};

const ChartSection = ({ title, children, subtitle }) => (
  <div className="gc-chart-section">
    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{title}</h4>
    {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{subtitle}</p>}
    {children}
  </div>
);

const LinesPerContributorChart = ({ contributors }) => {
  const [filters, setFilters] = useState({ backend: true, frontend: true, config: true });

  const toggleFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const chartData = useMemo(() => {
    const names = contributors.map(c => c.name);
    const datasets = [];

    if (filters.backend) {
      datasets.push({
        label: 'Backend',
        data: contributors.map(c => c.backend?.countOfLines || 0),
        backgroundColor: 'rgba(24, 80, 255, 0.8)',
        borderColor: '#1850ff',
        borderWidth: 2,
        borderRadius: 6
      });
    }
    if (filters.frontend) {
      datasets.push({
        label: 'Frontend',
        data: contributors.map(c => c.frontend?.countOfLines || 0),
        backgroundColor: 'rgba(81, 207, 102, 0.8)',
        borderColor: '#51cf66',
        borderWidth: 2,
        borderRadius: 6
      });
    }
    if (filters.config) {
      datasets.push({
        label: 'Config',
        data: contributors.map(c => c.config?.countOfLines || 0),
        backgroundColor: 'rgba(252, 196, 25, 0.8)',
        borderColor: '#fcc419',
        borderWidth: 2,
        borderRadius: 6
      });
    }

    return { labels: names, datasets };
  }, [contributors, filters]);

  const options = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: true, position: 'top', labels: { color: '#6b788d', font: { size: 12 } } },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: '#6b788d',
        font: { size: 11, weight: 'bold' },
        formatter: (value) => value > 0 ? value.toLocaleString() : ''
      }
    },
    scales: {
      y: { ...axisStyle, beginAtZero: true, title: { display: true, text: 'Lines', color: '#27303e', font: { size: 14, weight: 'bold' } } },
      x: xAxisStyle
    }
  };

  return (
    <ChartSection title="Lines of Code per Contributor" subtitle="Breakdown by backend, frontend, and config">
      <div className="flex gap-4 mb-4 flex-wrap">
        {['backend', 'frontend', 'config'].map(key => (
          <label key={key} className="gc-checkbox-label">
            <input
              type="checkbox"
              checked={filters[key]}
              onChange={() => toggleFilter(key)}
              className="gc-checkbox"
            />
            <span className="capitalize">{key}</span>
          </label>
        ))}
      </div>
      <div className="w-full h-[350px]">
        <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>
    </ChartSection>
  );
};

const SimpleBarChart = ({ contributors, dataKey, title, subtitle, yLabel, formatValue }) => {
  const chartData = useMemo(() => {
    const names = contributors.map(c => c.name);
    const values = contributors.map(c => {
      const val = c[dataKey];
      return val === null || val === undefined ? 0 : val;
    });

    return {
      labels: names,
      datasets: [{
        label: title,
        data: values,
        backgroundColor: contributors.map((_, i) => {
          const color = CHART_COLORS[i % CHART_COLORS.length];
          return color + 'cc';
        }),
        borderColor: contributors.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderWidth: 2,
        borderRadius: 6
      }]
    };
  }, [contributors, dataKey, title]);

  const options = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          label: (context) => {
            const val = context.parsed.y;
            return formatValue ? formatValue(val) : `${val}`;
          }
        }
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: '#6b788d',
        font: { size: 11, weight: 'bold' },
        formatter: (value) => value > 0 ? (value % 1 !== 0 ? value.toFixed(1) : value.toLocaleString()) : ''
      }
    },
    scales: {
      y: { ...axisStyle, beginAtZero: true, title: { display: true, text: yLabel || title, color: '#27303e', font: { size: 14, weight: 'bold' } } },
      x: xAxisStyle
    }
  };

  return (
    <ChartSection title={title} subtitle={subtitle}>
      <div className="w-full h-[350px]">
        <Bar data={chartData} options={options} plugins={[ChartDataLabels]} />
      </div>
    </ChartSection>
  );
};

const ChurnRatioSection = ({ contributors }) => (
  <ChartSection title="Churn Ratio" subtitle="Ratio of changed lines to total lines (max 1.0)">
    <div className="flex flex-col gap-4">
      {contributors.map((c, i) => {
        const ratio = c.churnRatio ?? 0;
        const percentage = Math.min(ratio * 100, 100);
        const color = ratio <= 0.2 ? '#51cf66' : ratio <= 0.5 ? '#fcc419' : '#ff6b6b';
        return (
          <div key={c.name} className="gc-churn-row">
            <div className="gc-churn-name">{c.name}</div>
            <div className="gc-churn-bar-bg">
              <div
                className="gc-churn-bar-fill"
                style={{ width: `${percentage}%`, backgroundColor: color }}
              />
            </div>
            <div className="gc-churn-value" style={{ color }}>{ratio.toFixed(2)}</div>
          </div>
        );
      })}
    </div>
  </ChartSection>
);

const CommitHourHeatmap = ({ contributor }) => {
  const heatmapData = useMemo(() => {
    if (!contributor.commitHourHeatmap) return null;
    const map = {};
    contributor.commitHourHeatmap.forEach(entry => {
      map[entry.day] = entry.hours;
    });
    return map;
  }, [contributor.commitHourHeatmap]);

  if (!heatmapData) return null;

  const maxValue = Math.max(
    ...Object.values(heatmapData).flat()
  );

  return (
    <div className="gc-heatmap-container">
      <div className="gc-heatmap-grid">
        <div className="gc-heatmap-day-labels">
          <div className="gc-heatmap-corner" />
          {DAY_ORDER.map((day, i) => (
            <div key={day} className="gc-heatmap-day-label">{DAY_LABELS[i]}</div>
          ))}
        </div>
        <div className="gc-heatmap-hours">
          <div className="gc-heatmap-hour-labels">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="gc-heatmap-hour-label">
                {h % 3 === 0 ? `${h}:00` : ''}
              </div>
            ))}
          </div>
          {DAY_ORDER.map(day => (
            <div key={day} className="gc-heatmap-row">
              {(heatmapData[day] || Array(24).fill(0)).map((val, h) => {
                const isWorkHour = h >= 9 && h < 18;
                const cellColor = val > 0 ? getHeatmapColor(val, maxValue) : (isWorkHour ? WORK_HOUR_BG : '#ebedf0');
                return (
                  <div
                    key={h}
                    className={`gc-heatmap-cell ${isWorkHour && val === 0 ? 'gc-work-hour-cell' : ''}`}
                    style={{ backgroundColor: cellColor }}
                    title={`${day} ${h}:00 - ${val} commit${val !== 1 ? 's' : ''}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <HeatmapNumberedLegend maxValue={maxValue} />
    </div>
  );
};

const ActivityHeatmap = ({ contributor }) => {
  const { months, maxValue } = useMemo(() => {
    if (!contributor.activity || contributor.activity.length === 0) {
      return { months: [], maxValue: 0 };
    }

    const activityMap = {};
    let max = 0;
    contributor.activity.forEach(a => {
      activityMap[a.date] = a.countOfCommits;
      if (a.countOfCommits > max) max = a.countOfCommits;
    });

    const dates = contributor.activity.map(a => new Date(a.date + 'T00:00:00'));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Build month-by-month calendar from minDate's month to maxDate's month
    const monthsData = [];
    const cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const endMonth = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);

    while (cursor <= endMonth) {
      const year = cursor.getFullYear();
      const month = cursor.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sun

      const days = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        days.push({
          day: d,
          dayOfWeek: new Date(year, month, d).getDay(),
          date: dateStr,
          value: activityMap[dateStr] || 0
        });
      }

      monthsData.push({
        label: new Date(year, month, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        firstDayOfWeek,
        days
      });

      cursor.setMonth(cursor.getMonth() + 1);
    }

    return { months: monthsData, maxValue: max };
  }, [contributor.activity]);

  if (months.length === 0) return <p className="text-sm text-gray-400 italic">No activity data</p>;

  return (
    <div className="gc-heatmap-container">
      <div className="gc-calendar-view">
        {months.map((month, mi) => (
          <div key={mi} className="gc-calendar-month">
            <div className="gc-calendar-month-title">{month.label}</div>
            <div className="gc-calendar-weekday-labels">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} className="gc-calendar-weekday-label">{d}</div>
              ))}
            </div>
            <div className="gc-calendar-grid">
              {/* Empty cells for offset */}
              {Array.from({ length: month.firstDayOfWeek }, (_, i) => (
                <div key={`empty-${i}`} className="gc-calendar-cell-empty" />
              ))}
              {month.days.map((day) => (
                <div
                  key={day.day}
                  className="gc-calendar-cell"
                  style={{ backgroundColor: getHeatmapColor(day.value, maxValue) }}
                  title={`${day.date}: ${day.value} commit${day.value !== 1 ? 's' : ''}`}
                >
                  <span className="gc-calendar-day-num">{day.day}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <HeatmapNumberedLegend maxValue={maxValue} />
    </div>
  );
};

const TouchedFilesList = ({ contributor }) => {
  const [expanded, setExpanded] = useState(false);

  if (!contributor.touchedFiles || contributor.touchedFiles.length === 0) return null;

  return (
    <div className="gc-touched-files">
      <button
        className="gc-touched-files-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        <svg
          className={`gc-toggle-icon ${expanded ? 'gc-toggle-icon-open' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
        <span>Touched Files ({contributor.touchedFiles.length})</span>
      </button>
      {expanded && (
        <div className="gc-touched-files-list">
          {contributor.touchedFiles.map((file, i) => (
            <div key={i} className="gc-touched-file-item">
              <svg className="gc-file-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4.75 3A1.75 1.75 0 003 4.75v10.5c0 .966.784 1.75 1.75 1.75h10.5A1.75 1.75 0 0017 15.25V7.44a1.75 1.75 0 00-.513-1.237l-3.69-3.69A1.75 1.75 0 0011.56 2H4.75z" />
              </svg>
              <span className="gc-file-path">{file}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GitContributors = ({ gitContributors }) => {
  const [expandedContributors, setExpandedContributors] = useState({});

  if (!gitContributors || !gitContributors.contributors || gitContributors.contributors.length === 0) {
    return null;
  }

  const { contributors, period } = gitContributors;

  const toggleContributor = (name) => {
    setExpandedContributors(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div className="p-6 m-6 bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-gray-200 dark:border-gray-700">
        <div className="text-3xl">👥</div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white m-0">Contributors</h2>
          {period && (
            <p className="text-sm text-gray-500 dark:text-gray-400 m-0 mt-1">
              {period.from} to {period.to} &middot; {contributors.length} contributor{contributors.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Aggregated charts */}
      <div className="gc-charts-grid">
        <LinesPerContributorChart contributors={contributors} />

        <SimpleBarChart
          contributors={contributors}
          dataKey="countOfCommits"
          title="Commits per Contributor"
          yLabel="Commits"
          formatValue={(v) => `${v} commits`}
        />

        <SimpleBarChart
          contributors={contributors}
          dataKey="countOfPRs"
          title="PRs per Contributor"
          yLabel="Pull Requests"
          formatValue={(v) => `${v} PRs`}
        />

        <ChurnRatioSection contributors={contributors} />

        <SimpleBarChart
          contributors={contributors}
          dataKey="averageDaysBetweenCommits"
          title="Avg Days Between Commits"
          subtitle="Lower is more frequent"
          yLabel="Days"
          formatValue={(v) => `${v} days`}
        />

        <SimpleBarChart
          contributors={contributors}
          dataKey="averagePrSizeLines"
          title="Avg PR Size (Lines)"
          subtitle="Average number of lines per pull request"
          yLabel="Lines"
          formatValue={(v) => `${v.toLocaleString()} lines`}
        />

        <SimpleBarChart
          contributors={contributors}
          dataKey="reviewTurnaroundDays"
          title="Review Turnaround (Days)"
          subtitle="Average time to review a PR"
          yLabel="Days"
          formatValue={(v) => `${v} days`}
        />
      </div>

      {/* Per-contributor details */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Individual Contributor Details</h3>
        <div className="flex flex-col gap-4">
          {contributors.map((contributor, i) => (
            <div key={contributor.name} className="gc-contributor-card">
              <button
                className="gc-contributor-header"
                onClick={() => toggleContributor(contributor.name)}
              >
                <div className="gc-contributor-avatar" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}>
                  {contributor.name.charAt(0).toUpperCase()}
                </div>
                <div className="gc-contributor-info">
                  <span className="gc-contributor-name">{contributor.name}</span>
                  <span className="gc-contributor-stats">
                    {contributor.countOfCommits} commits &middot; {contributor.countOfPRs} PRs &middot; {contributor.totalCountOfAffectedLines?.toLocaleString()} lines
                  </span>
                </div>
                <svg
                  className={`gc-toggle-icon ${expandedContributors[contributor.name] ? 'gc-toggle-icon-open' : ''}`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>

              {expandedContributors[contributor.name] && (
                <div className="gc-contributor-details">
                  <div className="gc-detail-section">
                    <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Commit Hour Heatmap</h5>
                    <CommitHourHeatmap contributor={contributor} />
                  </div>

                  <div className="gc-detail-section">
                    <h5 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Commit Day Heatmap</h5>
                    <ActivityHeatmap contributor={contributor} />
                  </div>

                  <TouchedFilesList contributor={contributor} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GitContributors;
